import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Users,
  Shield,
  UserPlus,
  Mail,
  CheckCircle,
  Clock3,
  RefreshCw,
  Trash2,
  ShieldCheck,
  Pencil,
  Crown
} from 'lucide-react';

const defaultMembers = [
  {
    id: 'owner-1',
    name: 'Admin Owner',
    email: 'admin@consistency.app',
    role: 'Owner',
    scopes: ['app_profiles', 'system_settings', 'billing'],
    status: 'Active',
    invited_at: '2024-12-01T12:00:00Z',
    accepted_at: '2024-12-01T12:05:00Z'
  }
];

export default function TeamPermissions() {
  const [primaryAdminEmail, setPrimaryAdminEmail] = useState('admin@consistency.app');
  const [pendingOwnerEmail, setPendingOwnerEmail] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Admin / Owner');
  const [inviteScopes, setInviteScopes] = useState(['app_profiles', 'creator_profiles']);
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState(defaultMembers);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const savedInvites = localStorage.getItem('admin_team_invites');
    const savedMembers = localStorage.getItem('admin_team_members');
    if (savedInvites) setInvites(JSON.parse(savedInvites));
    if (savedMembers) setMembers(JSON.parse(savedMembers));
  }, []);

  const saveState = (nextInvites, nextMembers) => {
    setInvites(nextInvites);
    setMembers(nextMembers);
    localStorage.setItem('admin_team_invites', JSON.stringify(nextInvites));
    localStorage.setItem('admin_team_members', JSON.stringify(nextMembers));
  };

  const handleInvite = () => {
    if (!inviteEmail) return;
    const now = new Date().toISOString();
    const newInvite = {
      id: `invite-${Date.now()}`,
      email: inviteEmail,
      role: inviteRole,
      scopes: inviteScopes,
      status: 'Pending',
      invited_at: now,
      accepted_at: null,
      expired_at: null
    };
    const nextInvites = [newInvite, ...invites];
    saveState(nextInvites, members);
    setInviteEmail('');
  };

  const handleOwnershipTransfer = () => {
    if (!pendingOwnerEmail) return;
    setPrimaryAdminEmail(pendingOwnerEmail);
    setPendingOwnerEmail('');
  };

  const selectedMember = useMemo(() => members.find((m) => m.id === selectedMemberId), [members, selectedMemberId]);

  const updateMemberScopes = (memberId, scopes) => {
    const nextMembers = members.map((m) => (m.id === memberId ? { ...m, scopes } : m));
    saveState(invites, nextMembers);
  };

  const updateInviteStatus = (inviteId, status) => {
    const timestamp = new Date().toISOString();
    const nextInvites = invites.map((i) =>
      i.id === inviteId
        ? {
            ...i,
            status,
            accepted_at: status === 'Accepted' ? timestamp : i.accepted_at,
            expired_at: status === 'Expired' ? timestamp : i.expired_at
          }
        : i
    );
    saveState(nextInvites, members);
  };

  const removeInvite = (inviteId) => {
    const nextInvites = invites.filter((i) => i.id !== inviteId);
    saveState(nextInvites, members);
  };

  const revokeMember = (memberId) => {
    const nextMembers = members.filter((m) => m.id !== memberId);
    saveState(invites, nextMembers);
    setSelectedMemberId(null);
  };

  const toggleScope = (scope) => {
    setInviteScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const scopeLabels = {
    app_profiles: 'App Profiles',
    creator_profiles: 'Creator Profiles',
    billing: 'Billing',
    system_settings: 'System Settings'
  };

  const renderScopes = (scopes) =>
    scopes.map((scope) => (
      <Badge key={scope} variant="outline" className="border-white/15 text-xs mr-1 mb-1">
        {scopeLabels[scope] || scope}
      </Badge>
    ));

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6">
      <div className="mb-2">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Users className="h-8 w-8 text-indigo-500" />
          Team & Permissions
        </h1>
        <p className="text-[#8A93A6]">Manage admin ownership, invites, roles, scopes, and audit history.</p>
      </div>

      {/* Ownership */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-400" />
            Primary Admin / Ownership
          </CardTitle>
          <CardDescription>Transfer or update the primary admin email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={primaryAdminEmail}
              onChange={(e) => setPrimaryAdminEmail(e.target.value)}
              className="bg-[#1C1F26] border-white/10 text-white"
              aria-label="Primary admin email"
            />
            <div className="flex-1">
              <Input
                placeholder="Transfer ownership to..."
                value={pendingOwnerEmail}
                onChange={(e) => setPendingOwnerEmail(e.target.value)}
                className="bg-[#1C1F26] border-white/10 text-white"
              />
            </div>
            <Button onClick={handleOwnershipTransfer} className="bg-indigo-500 hover:bg-indigo-600">
              Transfer
            </Button>
          </div>
          <p className="text-xs text-[#8A93A6]">
            Ownership change triggers a verification email. Current owner retains access until acceptance is completed.
          </p>
        </CardContent>
      </Card>

      {/* Invite & list */}
      <Card className="glass">
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-500" />
                Admin Team
              </CardTitle>
              <CardDescription>Invite admins, track pending, manage scopes and roles.</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="email"
                placeholder="email@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-[#1C1F26] border-white/10 text-white"
              />
              <select
                className="bg-[#1C1F26] border border-white/10 rounded-lg px-2 py-1 text-white"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option>Admin / Owner</option>
                <option>Beta Testers</option>
                <option>Investors</option>
                <option>Internal Team Members</option>
              </select>
              <div className="flex items-center gap-2 text-xs text-[#8A93A6]">
                {Object.keys(scopeLabels).map((scope) => (
                  <label key={scope} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="accent-indigo-500"
                      checked={inviteScopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                    />
                    {scopeLabels[scope]}
                  </label>
                ))}
              </div>
              <Button className="bg-indigo-500 hover:bg-indigo-600" onClick={handleInvite}>
                <UserPlus className="h-4 w-4 mr-2" />
                Invite
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Members */}
          <div className="space-y-2">
            {members.map((member) => (
              <button
                key={member.id}
                onClick={() => setSelectedMemberId(member.id)}
                className="w-full text-left p-4 rounded-lg bg-white/5 border border-white/10 hover:border-indigo-500/40 transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      {member.name}
                      {member.role === 'Owner' && <Badge className="border-amber-400/50 text-amber-300">Owner</Badge>}
                    </p>
                    <p className="text-sm text-[#8A93A6] flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      {member.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-indigo-500/30 text-indigo-500">
                      {member.role}
                    </Badge>
                    <Badge variant="outline" className="border-green-500/30 text-green-500">
                      {member.status}
                    </Badge>
                  </div>
                </div>
                <div className="mt-2 text-xs text-[#8A93A6] flex gap-1 flex-wrap">
                  {renderScopes(member.scopes)}
                </div>
                <div className="mt-2 text-xs text-[#8A93A6] flex gap-3">
                  <span>Invited: {new Date(member.invited_at).toLocaleString()}</span>
                  {member.accepted_at && <span>Accepted: {new Date(member.accepted_at).toLocaleString()}</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Invites */}
          {invites.length > 0 && (
            <div className="space-y-2">
              {invites.map((invite) => (
                <div key={invite.id} className="p-3 rounded-lg bg-white/5 border border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      {invite.email}
                      <Badge variant="outline" className="border-yellow-500/30 text-yellow-400">{invite.status}</Badge>
                    </p>
                    <p className="text-xs text-[#8A93A6] flex gap-2">
                      <span>Invited: {new Date(invite.invited_at).toLocaleString()}</span>
                      {invite.accepted_at && <span>Accepted: {new Date(invite.accepted_at).toLocaleString()}</span>}
                      {invite.expired_at && <span>Expired: {new Date(invite.expired_at).toLocaleString()}</span>}
                    </p>
                    <div className="mt-1 text-xs text-[#8A93A6] flex gap-1 flex-wrap">
                      {renderScopes(invite.scopes)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateInviteStatus(invite.id, 'Accepted')}>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Mark Accepted
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => updateInviteStatus(invite.id, 'Expired')}>
                      <Clock3 className="h-3 w-3 mr-1" />
                      Expire
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeInvite(invite.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member detail drawer (inline) */}
      {selectedMember && (
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-pink-500" />
              Manage Member
            </CardTitle>
            <CardDescription>Roles, scopes, and actions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-white font-medium">{selectedMember.name}</p>
                <p className="text-sm text-[#8A93A6]">{selectedMember.email}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => updateMemberScopes(selectedMember.id, Array.from(new Set([...selectedMember.scopes, 'system_settings'])))}>
                <ShieldCheck className="h-3 w-3 mr-1" />
                Grant System Settings
              </Button>
              <Button size="sm" variant="outline" onClick={() => updateMemberScopes(selectedMember.id, selectedMember.scopes.filter((s) => s !== 'system_settings'))}>
                <Shield className="h-3 w-3 mr-1" />
                Remove System Settings
              </Button>
              <Button size="sm" variant="outline" onClick={() => updateInviteStatus(selectedMember.id, 'Resent')}>
                <RefreshCw className="h-3 w-3 mr-1" />
                Resend Invite
              </Button>
              <Button size="sm" variant="destructive" onClick={() => revokeMember(selectedMember.id)}>
                <Trash2 className="h-3 w-3 mr-1" />
                Revoke / Remove
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-[#8A93A6]">Scopes:</p>
              <div className="flex gap-1 flex-wrap">{renderScopes(selectedMember.scopes)}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#8A93A6]">
              <div>Invited: {new Date(selectedMember.invited_at).toLocaleString()}</div>
              <div>Accepted: {selectedMember.accepted_at ? new Date(selectedMember.accepted_at).toLocaleString() : '—'}</div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-[#8A93A6]">Notes / Audit</p>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add admin notes about this member"
                className="bg-[#1C1F26] border-white/10 text-white"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Roles */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-pink-500" />
            Roles & Permissions
          </CardTitle>
          <CardDescription>Define access levels for team members</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <RoleCard
              title="Admin / Owner"
              badge="Full Access"
              badgeColor="border-pink-500/30 text-pink-500"
              description="Complete access to all features, settings, App Profiles, Creator Profiles, and System Settings."
            />
            <RoleCard
              title="Beta Testers"
              badge="Limited Access"
              badgeColor="border-indigo-500/30 text-indigo-500"
              description="Can preview new features across App Profiles and Creator Profiles without modifying System Settings."
            />
            <RoleCard
              title="Investors"
              badge="Read-only"
              badgeColor="border-blue-500/30 text-blue-500"
              description="Read-only visibility into Users, Revenue, Valuation, and App Profile defaults."
            />
            <RoleCard
              title="Internal Team Members"
              badge="Scoped Access"
              badgeColor="border-green-500/30 text-green-500"
              description="Operate within assigned App Profiles and Creator Profiles; cannot alter System Settings."
            />
            <RoleCard
              title="Scopes"
              badge="App Profiles + Creator Profiles"
              badgeColor="border-white/20 text-white"
              description="All role groups reference App Profiles and Creator Profiles for routing and approvals."
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RoleCard({ title, badge, badgeColor, description }) {
  return (
    <div className="p-4 rounded-lg bg-white/5 border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <p className="text-white font-medium">{title}</p>
        <Badge variant="outline" className={badgeColor}>{badge}</Badge>
      </div>
      <p className="text-sm text-[#8A93A6]">{description}</p>
    </div>
  );
}
